import React, {
  useContext,
  useRef,
  useState,
  useCallback,
  useEffect
} from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import BotBuilderNode from '../BotBuilderNode/BotBuilderNode';
import CodeBlockTreeNode from '../../../../types/BotBuilder/CodeBlockTreeNode';
import BotResponseTreeNode from '../../../../types/BotBuilder/BotResponseTreeNode';
import BotBuilderContext from '../../../../context/BotBuilderContext/BotBuilderContext';
import EnvironmentVariable from '../../../../types/BotBuilder/EnvironmentVariable';
import OutputVariableMapping from '../../../../types/BotBuilder/OutputVariableMapping';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import CodeEditor from './CodeEditor';
import EnvironmentVariables from './EnvironmentVariables';
import OutputMappings from './OutputMappings';
import Button from '../../../../components/Button/Button';

export type CodeBlockNodeData = {
  node: BotResponseTreeNode;
  highlightedHandle?: string | null;
  onOutcomeClick?: (nodeId: string, handleId: string) => void;
};

const isCodeBlockTreeNode = (content: any): content is CodeBlockTreeNode => {
  return content instanceof CodeBlockTreeNode;
};

function CodeBlockNode(
  props: NodeProps<CodeBlockNodeData>
): React.ReactElement {
  const { data, isConnectable } = props;
  const { onNodeUpdate, onNodeDelete, intent, slots, systemVariables } =
    useContext(BotBuilderContext);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  const codeBlockNode = isCodeBlockTreeNode(data.node.content)
    ? data.node.content
    : null;

  const [code, setCode] = useState<string>(codeBlockNode?.code ?? '');
  const [environmentVariables, setEnvironmentVariables] = useState<
    EnvironmentVariable[]
  >(codeBlockNode?.environmentVariables ?? []);
  const [outputMappings, setOutputMappings] = useState<OutputVariableMapping[]>(
    codeBlockNode?.outputMappings ?? []
  );

  const updateNodeData = useCallback(
    (updates: Partial<CodeBlockTreeNode>) => {
      if (!codeBlockNode) return;

      let hasChanges = false;
      Object.entries(updates).forEach(([key, value]) => {
        const currentValue = codeBlockNode[key as keyof CodeBlockTreeNode];
        if (JSON.stringify(currentValue) !== JSON.stringify(value)) {
          hasChanges = true;
        }
      });

      if (hasChanges) {
        Object.assign(codeBlockNode, updates);
        onNodeUpdate(data.node);
      }
    },
    [codeBlockNode, onNodeUpdate, data.node]
  );

  useEffect(() => {
    if (codeBlockNode) {
      setCode(codeBlockNode.code);
      setEnvironmentVariables(codeBlockNode.environmentVariables);
      setOutputMappings(codeBlockNode.outputMappings);
    }
  }, [
    codeBlockNode?.code,
    codeBlockNode?.environmentVariables,
    codeBlockNode?.outputMappings
  ]);

  useDebouncedEffect(
    () => {
      if (codeBlockNode && code !== codeBlockNode.code) {
        updateNodeData({ code });
      }
    },
    [code, codeBlockNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (
        codeBlockNode &&
        JSON.stringify(environmentVariables) !==
          JSON.stringify(codeBlockNode.environmentVariables)
      ) {
        updateNodeData({ environmentVariables });
      }
    },
    [environmentVariables, codeBlockNode, updateNodeData],
    300
  );

  useDebouncedEffect(
    () => {
      if (
        codeBlockNode &&
        JSON.stringify(outputMappings) !==
          JSON.stringify(codeBlockNode.outputMappings)
      ) {
        updateNodeData({ outputMappings });
      }
    },
    [outputMappings, codeBlockNode, updateNodeData],
    300
  );

  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
  }, []);

  const handleEnvironmentVariablesChange = useCallback(
    (newVariables: EnvironmentVariable[]) => {
      setEnvironmentVariables(newVariables);
    },
    []
  );

  const handleOutputMappingsChange = useCallback(
    (newMappings: OutputVariableMapping[]) => {
      setOutputMappings(newMappings);
    },
    []
  );

  const renderOutcomeBox = (
    label: string,
    handleId: string,
    colorClass: string
  ): React.ReactElement => {
    const isHighlighted = data.highlightedHandle === handleId;
    const highlightClass = isHighlighted
      ? 'ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
      : '';

    return (
      <div className='mb-2 relative'>
        <Button
          variant='outline'
          className={`p-3 border rounded-md cursor-pointer hover:opacity-80 transition-colors
            text-sm w-full justify-start text-left ${colorClass} ${highlightClass}`}
          onClick={(e) => {
            e.stopPropagation();
            data.onOutcomeClick?.(data.node.id, handleId);
          }}
        >
          {label}
        </Button>
        <Handle
          type='source'
          position={Position.Right}
          id={handleId}
          style={{ right: -26 }}
          isConnectable={isConnectable}
        />
      </div>
    );
  };

  return (
    <BotBuilderNode
      title='Code block'
      sourceNode={data.node}
      onNodeDelete={async () => {
        onNodeDelete(data.node);
      }}
      deleteButtonRef={deleteButtonRef}
      showAddButton={false}
    >
      <Handle
        type='target'
        position={Position.Top}
        style={{ top: -8 }}
        isConnectable={isConnectable}
        id='c'
      />

      <div className='flex flex-col gap-4 w-full bg-transparent p-0'>
        <CodeEditor
          code={code}
          onChange={handleCodeChange}
          intentSlots={intent?.slots}
          globalSlots={slots}
          systemVariables={systemVariables}
        />

        <EnvironmentVariables
          variables={environmentVariables}
          onChange={handleEnvironmentVariablesChange}
        />

        <OutputMappings
          mappings={outputMappings}
          onChange={handleOutputMappingsChange}
        />

        <div className='mt-4'>
          {renderOutcomeBox(
            'Success',
            `${data.node.id}-success-handle`,
            'bg-green-100 border-green-300'
          )}
          {renderOutcomeBox(
            'Error',
            `${data.node.id}-error-handle`,
            'bg-red-100 border-red-300'
          )}
        </div>
      </div>
    </BotBuilderNode>
  );
}

export default CodeBlockNode;
